import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { ShieldAlert, Mail, LogOut, Check, Loader2, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { copyToClipboard } from './utils/clipboard';
import {
  BanAppealService,
  cooldownEndsAt,
  APPEAL_MAX_LENGTH,
  type BanAppeal,
} from '../services/BanAppealService';

/**
 * The full-screen state a suspended account sees instead of the app.
 *
 * Replaces the whole shell rather than overlaying a banner: a dismissible notice
 * a banned user could tap past would leave them inside an app where every write
 * is denied by the security rules, which reads as the app being broken rather
 * than as a suspension. This is the explanation for what the rules are already
 * enforcing — the rules remain the actual control.
 *
 * The reason shown is whatever the Watcher typed at ban time (banUser's third
 * argument, stored as banReason). If it is missing the screen still says what
 * happened rather than falling back to a blank or a raw error.
 *
 * Contact is the address the legal screen already publishes, not a new channel.
 */
const SUPPORT_EMAIL = 'afroyapteam@gmail.com';

export function SuspendedScreen() {
  const { banState, userProfile, firebaseUser } = useUserData();
  const [copied, setCopied] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const expiresAt = banState.expiresAt ?? null;
  const isTemporary = !!expiresAt;

  const [appeal, setAppeal] = useState<BanAppeal | null>(null);
  const [loadingAppeal, setLoadingAppeal] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [appealText, setAppealText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const uid = firebaseUser?.uid || String(userProfile?.id || '');

  useEffect(() => {
    if (!uid) { setLoadingAppeal(false); return; }
    let cancelled = false;
    (async () => {
      const a = await BanAppealService.getMyAppeal(uid);
      if (!cancelled) { setAppeal(a); setLoadingAppeal(false); }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  const handleSubmitAppeal = async () => {
    if (!uid) return;
    setSubmitting(true);
    const res = await BanAppealService.submitAppeal(uid, appealText);
    if (res.success) {
      toast.success('Appeal submitted');
      setShowForm(false);
      setAppealText('');
      setAppeal(await BanAppealService.getMyAppeal(uid));
    } else {
      toast.error(res.error || 'Could not submit your appeal');
    }
    setSubmitting(false);
  };

  const waitUntil = cooldownEndsAt(appeal);

  // What the appeal section shows depends on where this account is in the cycle:
  // never appealed, waiting on a decision, denied and cooling down, or denied and
  // free to try again. A denied appeal must not just vanish — leaving the form
  // there with no explanation reads as the submission having done nothing.
  const appealView = appeal?.status === 'pending' ? (
    <div className="rounded-2xl p-4 bg-white/5 border border-white/10 text-left flex items-start gap-3">
      <Clock className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm text-white/85 font-semibold">Appeal under review</p>
        <p className="text-xs text-white/50 mt-1">
          We have your appeal and will look at it. You will regain access here if it
          is approved.
        </p>
      </div>
    </div>
  ) : appeal?.status === 'denied' ? (
    <div className="rounded-2xl p-4 bg-white/5 border border-white/10 text-left">
      <p className="text-sm text-white/85 font-semibold">Reviewed — appeal denied</p>
      {appeal.watcherNote ? (
        <p className="text-xs text-white/60 mt-1.5 break-words">{appeal.watcherNote}</p>
      ) : null}
      {waitUntil ? (
        <p className="text-xs text-white/45 mt-2">
          You can appeal again after {waitUntil.toLocaleDateString()}.
        </p>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="mt-3 text-xs font-bold text-[#4478ea]"
        >
          Appeal again
        </button>
      )}
    </div>
  ) : showForm ? (
    <div className="rounded-2xl p-4 bg-white/5 border border-white/10 text-left flex flex-col gap-2">
      <label className="text-[11px] uppercase tracking-wider text-white/40 font-bold">
        Why should this be reversed?
      </label>
      <textarea
        value={appealText}
        onChange={(e) => setAppealText(e.target.value.slice(0, APPEAL_MAX_LENGTH))}
        rows={4}
        autoFocus
        placeholder="Explain what happened, in your own words."
        className="w-full rounded-xl bg-black/30 border border-white/10 text-white text-sm p-3 outline-none resize-none"
      />
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-white/30">
          {appealText.length}/{APPEAL_MAX_LENGTH}
        </span>
        <div className="flex gap-2">
          <button onClick={() => setShowForm(false)} className="text-xs text-white/50 px-3 py-2">
            Cancel
          </button>
          <button
            onClick={handleSubmitAppeal}
            disabled={submitting || !appealText.trim()}
            className="text-xs font-bold text-white bg-[#4478ea] px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-40"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  ) : (
    <button
      onClick={() => setShowForm(true)}
      className="w-full h-12 rounded-xl bg-[#4478ea] text-white font-bold"
    >
      Appeal this suspension
    </button>
  );

  const handleCopyEmail = async () => {
    const ok = await copyToClipboard(SUPPORT_EMAIL);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy the address');
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      const { getAuthSafe } = await import('../services/firebaseConfig');
      const { signOut } = await import('firebase/auth');
      const auth = await getAuthSafe();
      await signOut(auth);
    } catch (e) {
      console.error('[SuspendedScreen] sign out failed:', e);
      toast.error('Could not sign out');
      setSigningOut(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center px-6"
      style={{ zIndex: 100000, backgroundColor: '#0d1b2a' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm flex flex-col items-center text-center gap-5"
      >
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-[#E74C3C]/15">
          <ShieldAlert className="w-8 h-8 text-[#E74C3C]" />
        </div>

        <div>
          <h1 className="text-xl font-bold text-white">Your account has been suspended</h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            This account was suspended for violating our community guidelines. You
            cannot post, message, or join rooms while it is suspended.
          </p>
        </div>

        {banState.reason ? (
          <div className="w-full rounded-2xl p-4 bg-white/5 border border-white/10 text-left">
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5">
              Reason given
            </p>
            <p className="text-sm text-white/85 break-words">{banState.reason}</p>
          </div>
        ) : null}

        <div className="w-full rounded-2xl p-4 bg-white/5 border border-white/10 text-left">
          <p className="text-[11px] uppercase tracking-wider text-white/40 font-bold mb-1.5">
            {isTemporary ? 'Suspended until' : 'Length'}
          </p>
          <p className="text-sm text-white/85">
            {isTemporary
              ? expiresAt!.toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })
              : 'This suspension does not expire on its own.'}
          </p>
        </div>

        {/* Appeal. Writes to ban_appeals, which is the one thing a suspended
            account may write — a narrow carve-out in the rules layered on top of
            the app-wide deny, not a loosening of it. */}
        <div className="w-full">
          {loadingAppeal ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : appealView}
        </div>

        <div className="w-full flex flex-col gap-2">
          <p className="text-xs text-white/50">Or contact us directly:</p>
          <button
            onClick={handleCopyEmail}
            className="w-full h-11 rounded-xl bg-white/10 text-white text-sm font-bold flex items-center justify-center gap-2"
          >
            {copied ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
            {copied ? 'Address copied' : SUPPORT_EMAIL}
          </button>
          {userProfile?.username ? (
            <p className="text-[11px] text-white/35">
              Your username: @{String(userProfile.username).replace(/^@/, '')}
            </p>
          ) : firebaseUser?.uid ? (
            <p className="text-[11px] text-white/35">Account ID: {firebaseUser.uid}</p>
          ) : null}
        </div>

        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="text-sm text-white/45 py-2 flex items-center gap-1.5 disabled:opacity-50"
        >
          <LogOut className="w-3.5 h-3.5" />
          {signingOut ? 'Signing out...' : 'Sign out'}
        </button>
      </motion.div>
    </div>
  );
}
